import { useQuery, useSuspenseQuery } from "@apollo/client";
import { useMemo } from "react";
import { useParams } from "react-router";
import { PROCESSED_MANIFESTS_QUERY } from "routes/Customer/Waste/Manifests/api";

// `tenantIdOverride` lets cross-tenant staff callers (the Manifest Review queue, off
// any tenant-scoped route) pass the manifest's tenant explicitly; customer callers omit
// it and fall back to the route's tenantId.
export const useProcessedManifest = (
  processedManifestId: string | undefined,
  tenantIdOverride?: string
) => {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdOverride ?? routeTenantId;
  const { data, loading } = useQuery(PROCESSED_MANIFESTS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${processedManifestId}`,
      page: 0,
      pageSize: 1,
    },
    skip: !tenantId || !processedManifestId,
  });

  return useMemo(() => {
    return {
      data,
      loading,
      processedManifest: data?.processedManifests?.items?.[0],
    };
  }, [data, loading]);
};

export const useProcessedManifestSuspense = (
  processedManifestId: string | undefined,
  tenantIdOverride?: string
) => {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdOverride ?? routeTenantId;
  const { data, refetch } = useSuspenseQuery(PROCESSED_MANIFESTS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} id:${processedManifestId}`,
      page: 0,
      pageSize: 1,
    },
    skip: !tenantId || !processedManifestId,
  });

  return {
    data,
    // Scoped to this manifest's variables — refetches only this record, not the
    // other (list-grid) instances of the shared query. Wrap calls in
    // startTransition to avoid re-triggering the Suspense fallback.
    refetch,
    processedManifest: data?.processedManifests?.items?.[0],
  };
};
